import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { CameraOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { Button, Image } from 'antd';
import ScanditIdCapture from 'common/components/id-capture/scandit-id-capture';
import { IIdDocumentData } from 'common/components/id-capture/scandit-id-capture.types';
import { IDriveIdProps } from './drive-id.types';
import { notification } from 'common/utils/notification';
import configurationStore from 'common/stores/configuration/configuration.store';
import { IIdDocumentRead } from 'common/services/guest-service/guest.service.types';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import EIQImage from 'common/components/image/eiq-image';

const DriveId = forwardRef(({ onUpdateFromCamera, onImageRecognized, idDocument }: IDriveIdProps, ref: any) => {
  const [idDocumentData, setIdDocumentData] = useState<IIdDocumentData | null | undefined>();
  const [idDocumentRead, setIdDocumentRead] = useState<IIdDocumentRead | null | undefined>(idDocument);

  const [isDocumentRecognitionFailed, setIsDocumentRecognitionFailed] = useState(false);

  const getSnapshot = () => {
    const container = document.getElementById('scandit-scanner-wrapper-id');
    if (!container) {
      return null;
    }

    const video = container.querySelector('video');
    if (!video || !video.videoWidth) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx!.drawImage(video, 0, 0);

    return canvas.toDataURL('image/jpeg');
  };

  const getIdDocument = () => {
    if (idDocumentRead) {
      return null;
    }

    if (idDocumentData) {
      return idDocumentData;
    }

    const snapshotBase64 = getSnapshot();

    if (!snapshotBase64) {
      return null;
    }

    const result: IIdDocumentData = {
      type: 'snapshot',
      imageBase64: snapshotBase64,
      firstName: '',
      lastName: '',
      secondaryLastName: null,
      gender: null,
      dateOfBirth: null,
      nationality: null,
      address: null,
      issuingCountry: null,
      issuingCountryIso: null,
      number: null,
      additionalNumber: null,
      expiredAt: null,
      issuedAt: null,
    };

    return result;
  };

  useImperativeHandle(ref, () => ({
    getIdDocument,
  }));

  const onIdDocumentRecognized = async (document: IIdDocumentData) => {
    notification.destroy();
    setIsDocumentRecognitionFailed(false);
    setIdDocumentData(document);

    if (onImageRecognized) {
      onImageRecognized(document);
    }
  };

  useEffect(() => {
    if (!isDocumentRecognitionFailed) {
      return;
    }

    if (!configurationStore.currentUserGate?.useRtsp) {
      notification.destroy();
      notification.warning({ message: 'ID not recognized. Leave the ID on the tray while approving the guest.' });
    }
  }, [isDocumentRecognitionFailed]);

  const onIdDocumentRecognizeFailed = (message: string) => {
    setIsDocumentRecognitionFailed(true);
  };

  const onUpdateFromCameraClicked = () => {
    if (onUpdateFromCamera) {
      onUpdateFromCamera();
    }

    if (idDocumentRead) {
      setIdDocumentRead(null);
    }
    if (idDocumentData) {
      setIdDocumentData(null);
    }
  };

  const getIdDocumentSection = () => {
    if (idDocumentRead) {
      return <EIQImage url={idDocumentRead.imageUrl} fileType={UploadFileTypes.IdDocument} alt="Id document img" />;
    }

    if (idDocumentData) {
      return <Image src={`data:image/jpeg;base64,${idDocumentData.imageBase64}`} loading="lazy" />;
    } else {
      return <ScanditIdCapture onRecognized={onIdDocumentRecognized} onFailed={onIdDocumentRecognizeFailed} />;
    }
  };

  return (
    <div className="instructions-item">
      <div className="instructions-item-header">
        <span className="title">Photo ID</span>
        <Button icon={<CameraOutlined />} onClick={onUpdateFromCameraClicked}>
          Update from Camera
        </Button>
      </div>
      {getIdDocumentSection()}
    </div>
  );
});

export default observer(DriveId);
